import React, {
	createContext,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";
import type { TagMinMax } from "../hooks/useSeriesMinMax";
import type {
	ChartConfig,
	CursorData,
	View,
	ViewTagsMap,
	scopeType,
} from "../models/TrendingModelsV2";
import { readTrendingUrlState } from "../utils/trendingUrlState";

const CURSOR_MIN_SEPARATION_MS = 1000;

interface TrendingState {
	viewTags: ViewTagsMap;
	viewSelected: View | null;
	views: View[];
	timeScopeStart: Date;
	timeScopeEnd: Date;
	scope: scopeType;
	shouldRefetchSeries: boolean;
	cursorData: CursorData | null;
	chartConfig: ChartConfig;
	seriesMinMax: TagMinMax;
	seriesPointCounts: Record<number, number>;
	chartPlotWidth: number;
	highlightedSeries: string | null;
	cursor1Time: number; // Cursor 1 timestamp (red cursor)
	cursor2Time: number; // Cursor 2 timestamp (blue cursor)
	selectedCursor: "cursor1" | "cursor2" | null; // Currently selected cursor for keyboard control
}

interface TrendingActions {
	setViewTags: (viewTags: ViewTagsMap) => void;
	setViewTagsAndRefetch: (viewTags: ViewTagsMap) => void;
	setViewSelected: (view: View | null) => void;
	setViews: (views: View[]) => void;
	setTotalScope: (scope: {
		start?: Date;
		end?: Date;
		scope?: scopeType;
		graphPan?: boolean;
	}) => void;
	setCursorData: (data: CursorData | null) => void;
	setChartConfig: (config: Partial<ChartConfig>) => void;
	triggerSeriesRefetch: () => void;
	setSeriesMinMax: (minMax: TagMinMax) => void;
	setSeriesPointCounts: (counts: Record<number, number>) => void;
	setChartPlotWidth: (width: number) => void;
	highlightSeries: (seriesName: string | null) => void;
	updateCursor1Time: (time: number) => void;
	updateCursor2Time: (time: number) => void;
	setSelectedCursor: (cursor: "cursor1" | "cursor2" | null) => void;
	resetCursors: () => void; // Reset cursors to default positions (10% and 90%)
}

interface TrendingContextValue {
	state: TrendingState;
	actions: TrendingActions;
}

const TrendingContext = createContext<TrendingContextValue | undefined>(
	undefined,
);

const getDefaultTimeScope = () => {
	const end = new Date();
	const start = new Date(end.getTime() - 8 * 60 * 60 * 1000); // 8 hours ago
	return { start, end, period: "1 hour" as scopeType };
};

export const TrendingProviderV2: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [initialUrlState] = useState(readTrendingUrlState);

	const initialScope = initialUrlState.timeScope
		? {
				start: new Date(initialUrlState.timeScope.start),
				end: new Date(initialUrlState.timeScope.end),
				period: initialUrlState.timeScope.period,
			}
		: getDefaultTimeScope();

	const [viewTags, setViewTags] = useState<ViewTagsMap>({});
	const [viewSelected, setViewSelected] = useState<View | null>(null);
	const [views, setViews] = useState<View[]>([]);
	const [timeScopeStart, setTimeScopeStart] = useState<Date>(
		initialScope.start,
	);
	const [timeScopeEnd, setTimeScopeEnd] = useState<Date>(initialScope.end);
	const [scope, setScope] = useState<scopeType>(initialScope.period);
	const [shouldRefetchSeries, setShouldRefetchSeries] = useState(false);
	const [cursorData, setCursorData] = useState<CursorData | null>(null);
	const [chartConfig, setChartConfigState] = useState<ChartConfig>({
		showLegend: false,
		showTooltip: true,
		animation: false,
		theme: "light",
	});
	const [seriesMinMax, setSeriesMinMax] = useState<TagMinMax>({});
	const [seriesPointCounts, setSeriesPointCounts] = useState<
		Record<number, number>
	>({});
	const [chartPlotWidth, setChartPlotWidth] = useState<number>(1000);
	const [highlightedSeries, setHighlightedSeries] = useState<string | null>(
		null,
	);

	const [cursor1Time, setCursor1TimeState] = useState<number>(
		initialUrlState.cursors?.cursor1 ?? initialScope.start.getTime(),
	);
	const [cursor2Time, setCursor2TimeState] = useState<number>(
		initialUrlState.cursors?.cursor2 ?? initialScope.end.getTime(),
	);
	const urlCursorsRef = useRef(initialUrlState.cursors);
	const [selectedCursor, setSelectedCursorState] = useState<
		"cursor1" | "cursor2" | null
	>(null);

	const state = useMemo<TrendingState>(
		() => ({
			viewTags,
			viewSelected,
			views,
			timeScopeStart,
			timeScopeEnd,
			scope,
			shouldRefetchSeries,
			cursorData,
			chartConfig,
			seriesMinMax,
			seriesPointCounts,
			chartPlotWidth,
			highlightedSeries,
			cursor1Time,
			cursor2Time,
			selectedCursor,
		}),
		[
			viewTags,
			viewSelected,
			views,
			timeScopeStart,
			timeScopeEnd,
			scope,
			shouldRefetchSeries,
			cursorData,
			chartConfig,
			seriesMinMax,
			seriesPointCounts,
			chartPlotWidth,
			highlightedSeries,
			cursor1Time,
			cursor2Time,
			selectedCursor,
		],
	);

	const resetCursorsToDefault = React.useCallback(() => {
		const urlCursors = urlCursorsRef.current;
		const urlTimeScope = initialUrlState.timeScope;
		const scopeStillMatchesUrl =
			urlTimeScope !== null &&
			urlTimeScope.start === timeScopeStart.getTime() &&
			urlTimeScope.end === timeScopeEnd.getTime();

		if (urlCursors !== null && scopeStillMatchesUrl) {
			setCursor1TimeState(urlCursors.cursor1);
			setCursor2TimeState(urlCursors.cursor2);
			return;
		}

		urlCursorsRef.current = null;
		setCursor1TimeState(timeScopeStart.getTime());
		setCursor2TimeState(timeScopeEnd.getTime());
	}, [initialUrlState, timeScopeStart, timeScopeEnd]);

	React.useEffect(() => {
		resetCursorsToDefault();
	}, [resetCursorsToDefault]);

	const actions = useMemo<TrendingActions>(
		() => ({
			setViewTags,

			setViewTagsAndRefetch: (newViewTags: ViewTagsMap) => {
				setViewTags(newViewTags);
				setShouldRefetchSeries((prev) => !prev);
			},

			setViewSelected,

			setViews,

			setTotalScope: ({
				start,
				end,
				scope: newScope,
				graphPan = false,
			}: {
				start?: Date;
				end?: Date;
				scope?: scopeType;
				graphPan?: boolean;
			}) => {
				if (start) setTimeScopeStart(start);
				if (end) setTimeScopeEnd(end);
				if (newScope) setScope(newScope);
				setShouldRefetchSeries((prev) => !prev);
			},

			setCursorData,

			setChartConfig: (config: Partial<ChartConfig>) => {
				setChartConfigState((prev) => ({ ...prev, ...config }));
			},

			triggerSeriesRefetch: () => {
				setShouldRefetchSeries((prev) => !prev);
			},

			setSeriesMinMax,

			setSeriesPointCounts,

			setChartPlotWidth,

			highlightSeries: (seriesName: string | null) => {
				setHighlightedSeries(seriesName);
			},

			updateCursor1Time: (time: number) => {
				// Constrain cursor1 to chart bounds and maintain separation from cursor2
				const constrainedTime = Math.max(
					timeScopeStart.getTime(),
					Math.min(time, cursor2Time - CURSOR_MIN_SEPARATION_MS),
				);

				urlCursorsRef.current = null;
				setCursor1TimeState(constrainedTime);
			},

			updateCursor2Time: (time: number) => {
				// Constrain cursor2 to chart bounds and maintain separation from cursor1
				const constrainedTime = Math.min(
					timeScopeEnd.getTime(),
					Math.max(time, cursor1Time + CURSOR_MIN_SEPARATION_MS),
				);

				urlCursorsRef.current = null;
				setCursor2TimeState(constrainedTime);
			},

			setSelectedCursor: setSelectedCursorState,

			resetCursors: resetCursorsToDefault,
		}),
		[
			timeScopeStart,
			timeScopeEnd,
			cursor1Time,
			cursor2Time,
			resetCursorsToDefault,
		],
	);

	const value = useMemo(
		() => ({
			state,
			actions,
		}),
		[state, actions],
	);

	return (
		<TrendingContext.Provider value={value}>
			{children}
		</TrendingContext.Provider>
	);
};

export const useTrendingContextV2 = () => {
	const context = useContext(TrendingContext);
	if (!context) {
		throw new Error(
			"useTrendingContextV2 must be used within TrendingProviderV2",
		);
	}
	return context;
};
